import _ from 'lodash'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'

import { Button, Divider, List, ListItem, ListItemButton, ListItemText, Stack, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'

import NotificationMenu from '@/components/ui/menu/NotificationMenu/NotificationMenu'

import { Event, EventObjectType, EventType } from '@/core/models/notification.model'
import { useGetMyNotificationsQuery, useMarkNotificationsAsObservedMutation } from '@/core/services/auth'
import { useGetNetworkQuery } from '@/core/services/network'
import { useGetTimeTrackingConsentQuery, useGetTimeTrackingNotificationsQuery, useMarkTimeTrackingNotificationReadMutation } from '@/core/api'

import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isStaff } from '@/utils/type-guards'

import { MAIN_HEADER_HEIGHT } from '../../model/constants'

export function Notifications() {
  const { enqueueSnackbar } = useSnackbar()
  const { t: tI18n } = useTranslation()
  const navigate = useNavigate()

  const authUser = useAppSelector(selectAuthUser)
  const isStaffMember = isStaff(authUser)

  const { data } = useGetMyNotificationsQuery({
    cacheKey: 'header-notifications',
    limit: 5,
    skip: 0,
  })
  const [markNotificationsAsSeen] = useMarkNotificationsAsObservedMutation()

  // Time tracking notifications
  const { data: consent } = useGetTimeTrackingConsentQuery(undefined, { skip: !isStaffMember })
  const skipTimeTracking = !isStaffMember || !consent?.enabled
  const { data: timeTrackingNotifications = [] } = useGetTimeTrackingNotificationsQuery(undefined, { skip: skipTimeTracking })
  const [markTimeTrackingAsRead] = useMarkTimeTrackingNotificationReadMutation()

  const events = useMemo(() => data?.items ?? [], [data])

  const unseenCount = useMemo(() => {
    const eventUnseen = events.filter((event) => !event.recipients.find((recipient) => recipient.userId === authUser?._id)?.seen).length
    const timeTrackingUnseen = timeTrackingNotifications.filter((n) => !n.read).length
    return eventUnseen + timeTrackingUnseen
  }, [authUser, events, timeTrackingNotifications])

  const maxHeight = `calc(100vh - ${MAIN_HEADER_HEIGHT}px - 16px)`

  return (
    <NotificationMenu
      onClose={async () => {
        const unreadEventIds = _.map(
          events.filter((event) => !event.recipients.find((recipient) => recipient.userId === authUser?._id)?.seen),
          '_id'
        )

        if (unreadEventIds.length) {
          try {
            await markNotificationsAsSeen({ eventIds: unreadEventIds, state: 'seen' }).unwrap()
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, tI18n('notification.notification.markAsRead.error')), { variant: 'error' })
          }
        }

        // Mark time tracking notifications as read
        timeTrackingNotifications.filter((n) => !n.read).forEach((n) => {
          markTimeTrackingAsRead(n._id).catch(() => {})
        })
      }}
      maxHeight={maxHeight}
      unseenCount={unseenCount}
    >
      <Stack height="100%" maxHeight={maxHeight}>
        <Typography variant="h6" component="div" align="center" sx={{ p: 1 }}>
          {tI18n('header.notificationMenu.title')}
        </Typography>
        <Divider />
        <Stack flex={1} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
          {/* Time tracking notifications */}
          {timeTrackingNotifications.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t`Time Tracking`}
                </Typography>
              </Stack>
              <List disablePadding>
                {timeTrackingNotifications.map((n) => (
                  <ListItem key={n._id} disablePadding>
                    <ListItemButton
                      onClick={async () => {
                        await markTimeTrackingAsRead(n._id).unwrap().catch(() => {})
                        navigate('/time-tracking')
                      }}
                      sx={[!n.read && { bgcolor: 'action.hover' }]}
                    >
                      <ListItemText
                        primary={n.message}
                        secondary={new Date(n.createdAt).toLocaleString()}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Divider />
            </>
          )}
          {/* General notifications */}
          <List disablePadding>
            {events.map((event) => (
              <NotificationItem key={event._id} event={event} />
            ))}
          </List>
        </Stack>
        <Divider />
        <Stack alignItems="center" justifyContent="center" py={1}>
          <Button component={Link} to="/notifications" color="primary">
            {tI18n('header.notificationMenu.viewAllNotificationButton.label')}
          </Button>
        </Stack>
      </Stack>
    </NotificationMenu>
  )
}

export function NotificationItem({ event }: { event: Event }) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useGetNetworkQuery(event.object_id, { skip: event.object_type !== EventObjectType.NETWORK })
  const [markNotificationsAsRead] = useMarkNotificationsAsObservedMutation()

  const label = useMemo(() => {
    switch (event.type) {
      case EventType.NETWORK_INVITATION_SENT:
        const sender = event.context?.sender
        return t(`header.notificationMenu.event.${event.type}`, {
          network: network?.name ?? '',
          sender: sender ? `${sender.userInfo.name} ${sender.userInfo.surname}` : '',
        })
      case EventType.NETWORK_DAILY_SUMMARY:
      case EventType.NETWORK_CONSULTATION_ORDER:
      case EventType.NETWORK_EXPERTISE_ORDER:
      case EventType.NETWORK_ORDER_ACCEPTED:
        return t(`header.notificationMenu.event.${event.type}`, { network: network?.name ?? '' })
      default:
        return t(`header.notificationMenu.event.${event.type}`)
    }
  }, [event, network, t])

  const redirectUrl = useMemo(() => {
    switch (event.type) {
      case EventType.NETWORK_CONSULTATION_ORDER:
      case EventType.NETWORK_EXPERTISE_ORDER:
      case EventType.NETWORK_ORDER_ACCEPTED:
        return `/networks/${event.context?.network}`
    }
    switch (event.object_type) {
      case EventObjectType.CONSULTATION:
        return `/consultations/${event.object_id}`
      case EventObjectType.NETWORK:
        return `/networks/${event.object_id}`
      case EventObjectType.PATIENT:
        return `/patients/${event.object_id}`
      case EventObjectType.TELEXPERTISE:
        return `/expertises/${event.object_id}`
      case EventObjectType.ORDER:
        return `/meeting-links/orders`
      default:
        return '/'
    }
  }, [event])

  return (
    <NotificationMenu.Item
      createdAt={event.createdAt}
      isRead={!!event.recipients.find((recipient) => recipient.userId === authUser?._id)?.read}
      label={label}
      onClick={async () => {
        navigate(redirectUrl)
        try {
          await markNotificationsAsRead({ eventIds: [event._id], state: 'read' }).unwrap()
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.notification.markAsRead.error')), { variant: 'error' })
        }
      }}
    />
  )
}
